import { create } from "zustand";
import { getDeviceType, ObjectEntries, TypedMap, type DeviceType, } from "@bracket/sharedTypes";
import { FlexAlignment, CSSSpaceDeclaration, } from "@bracket/ui/types";
import * as ACCESSORS from "./accessors";
import type { TabularRasaAccessorFn, } from "./types";
import React from "react";



export declare type RasaColumnOptions<T extends object> = {
	label?: string;
	sortable?: boolean;
	sort?: (a: T, b: T) => number;
	justify?: FlexAlignment;
	hidden?: boolean;
	width?: CSSSpaceDeclaration;
}


export declare type ComputedRasaColumnOptions<T extends object> = RasaColumnOptions<T> & {
	$basisPx?: number;
	$grow?: number;
	$firstPass: boolean;
	$width?: string;
	visualIdx?: number;
	accessor?: keyof typeof ACCESSORS | TabularRasaAccessorFn<keyof T, T>;
	accessorData?: Record<string, any>;
}


export declare type TabularRasaStore<T extends object> = {
	accessors?: Partial<Record<keyof T, keyof typeof ACCESSORS | TabularRasaAccessorFn<keyof T, T>>>;
	accessorData?: Record<string, any>;
	data: T[];
	pk?: keyof T;
	page: T[];
	currentPage: number;
	totalPages: number;
	totalItems: number;
	itemsPerPage: number;
	firstPage: number;
	lastPage: number;
	widths: number[];
	ready: boolean;
	paginating: boolean;
	deviceType: DeviceType;
	emptyDataSet: number;
	hiddenKeys: Set<string>;
	columnKeys: Map<keyof T, ComputedRasaColumnOptions<T>>;
	columnWidths: number[];
	firstPass: boolean;
	rowMargin?: string;
	rowPadding?: string;
	headerRowMargin?: string;
	headerRowPadding?: string;
	bodyRowMargin?: string;
	bodyRowPadding?: string;
	nextPage: () => boolean;
	previousPage: () => boolean;
	toPage: ( page: number ) => boolean;
	setDeviceType: ( deviceType?: DeviceType ) => void;
	sortOn: ( key: keyof T | ((a: T, b: T) => number) ) => void;
	sorters: Record<keyof T, (a: T, b: T) => number>;
	sorting_asc: Array<keyof T>;
	sorting_desc: Array<keyof T>;
	_bindRefs: ( ref: React.RefObject<HTMLDivElement> ) => void;
};


export declare type TabularRasaStoreOptions<T extends object> = {
	id: string;
	data: T[];
	paginate?: boolean;
	pageSize?: number;
	columns?: RasaColumnOptions<T>;
	sort?: keyof T | ((a: T, b: T) => number);
	pk?: keyof T;

	rowMargin?: string;
	rowPadding?: string;
	headerRowMargin?: string;
	headerRowPadding?: string;

	accessors?: Partial<Record<keyof T, keyof typeof ACCESSORS | TabularRasaAccessorFn<keyof T, T>>>;
	accessorData?: Record<string, any>;
}

export const createTabularRasaStore = <T extends object>({
	data,
	id,
	paginate = true,
	pageSize = 100,
	sort,
	columns,
	accessors,
	accessorData,
	pk,
}: TabularRasaStoreOptions<T>) => create<TabularRasaStore<T>>()((set, get) => {

	let __listening = false;
	let __ref: React.RefObject<HTMLDivElement> | null = null;
	let __timer: NodeJS.Timeout | null = null;


	const fetch = async () => {
		await new Promise(r => r(true));
		_applyInitialSort();
		_applyColumnOptions();
		_applyPagination();
		setDeviceType();
		_bindAccessors();
		if(__listening) globalThis.removeEventListener("resize", __onWindowResize);
		globalThis.addEventListener("resize", __onWindowResize);
		set({
			pk,
			ready: true,
		});

	}

	const setDeviceType = ( deviceType?: DeviceType ) => {
		const userAgent =
			typeof navigator !== "undefined"
				? navigator.userAgent
				: typeof process !== "undefined" && typeof process.version === "string"
					? `node/${process.version}`
					: "";
		set({
			deviceType: deviceType || getDeviceType(userAgent),
		});
	}

	const sortOn = ( key: keyof T, direction?: "asc" | "desc" ) => {
		const { sorters, sorting_asc:asc, sorting_desc:desc, data, currentPage, } = get();
		const in_asc = asc.includes(key);
		const in_desc = desc.includes(key);
		let next_asc = !in_asc && !in_desc ? [ ...asc, key] : in_asc ? asc.filter(k => k !== key) : asc;
		let next_desc = !in_asc && !in_desc ? desc : in_asc ? [...desc, key] : desc.filter(k => k !== key);
		const reverse = next_asc.includes(key) ? false : next_desc.includes(key) ? true : false;
		const next = [ ...data, ];
		let result = [ ...next_asc, ...next_desc ].reduce((acc, key) => acc.sort((a, b) => sorters[key](a, b)), next);
		if(reverse) result = result.reverse();
		const page = result.slice((currentPage-1) * pageSize, currentPage * pageSize);
		set({
			sorting_asc: next_asc,
			sorting_desc: next_desc,
			page,
			data: result,
		})
	}

	const toPage = ( pageNum: number ) => {
		const { totalPages, } = get();
		if(pageNum < 1 || pageNum > totalPages) return false;
		const page = data.slice((pageNum - 1) * pageSize, pageNum * pageSize);
		set({
			currentPage: pageNum,
			page,
		});
		return true;
	}

	const nextPage = (): boolean => {

		const { currentPage, totalPages, } = get();
		if(currentPage >= totalPages) return false;
		const nextPage = currentPage + 1;
		const page = data.slice((nextPage - 1) * pageSize, nextPage * pageSize);
		set({
			currentPage: nextPage,
			page,
		});
		return true;
	}

	const previousPage = (): boolean => {
		const { currentPage, } = get();
		if(currentPage === 1) return false;
		const previousPage = currentPage - 1;
		const page = data.slice((previousPage - 1) * pageSize, previousPage * pageSize);
		set({
			currentPage: previousPage,
			page,
		});
		return true;
	}

	const _applyPagination = () => {

		const { data, } = get();
		if(!paginate) {
			set({ page: data, });
			return;
		}
		const totalPages = Math.ceil(data.length / pageSize);
		set({
			currentPage: 1,
			totalPages,
			totalItems: data.length,
			itemsPerPage: pageSize,
			firstPage: 1,
			lastPage: totalPages,
			page: data.slice(0, pageSize),
		});
	}

	const _applyColumnOptions = () => {
		if(!columns) return;
		const { columnKeys, } = get();
		let cnt = 0;
		let sorters: Record<keyof T, (a: T, b: T) => number> = {} as Record<keyof T, (a: T, b: T) => number>;
		for(const [key, value] of ObjectEntries(columns)) {
			if(value.sortable) {
				sorters[key as keyof T] = value.sort || ((a, b) => a[key as keyof T] > b[key as keyof T] ? 1 : -1);
			}
			const visualIdx = value.hidden ? undefined : cnt++;
			columnKeys.set(key as keyof T, { $firstPass: true, $width: value.width, visualIdx, ...value as RasaColumnOptions<T> });
		}
		set({
			sorters,
			columnKeys,
		})
	}

	const _applyInitialSort = () => {
		if(!sort) return;
		const { data, } = get();
		if(typeof sort === "function") {
			const next = Array.from(data).sort(sort);
			set({
				data: next,
			});
		}
	}

	const _bindAccessors = () => {
		const { accessors, accessorData, columnKeys, } = get();
		if(accessors) {
			for(const [key, value] of ObjectEntries<Partial<Record<keyof T, keyof typeof ACCESSORS | TabularRasaAccessorFn<keyof T, T>>>>(accessors)) {
				const column = columnKeys.get(key);
				if(!column){
					console.warn(`Column ${key.toString()} not found whilst attempting to bind accessor ${value}`);
					continue;
				}
				if(typeof value === "function") {
					column.accessor = value;
					column.accessorData = accessorData;
					columnKeys.set(key as keyof T, column);
				}
				else if(typeof value === "string" && value in ACCESSORS) {
					const accessor = ACCESSORS[value as keyof typeof ACCESSORS];
					if(typeof accessor === "function") {
						column.accessor = accessor;
						column.accessorData = accessorData;
						continue;
					}
					throw new Error(`Accessor ${value} is not a function`);
				}
			}
			set({
				accessors,
				columnKeys,
			});
		}
	}


	const _bindRefs = ( ref: React.RefObject<HTMLDivElement> ) => {
		if(__ref) __ref = null;

		const widths = Array.from(ref.current?.children || []).map((child) => child.getBoundingClientRect().width);
		const { columnKeys, } = get();
		for(let i = 0; i < widths.length; i++) {
			const width = widths[i];
			const [ key, column, ] = Array.from(columnKeys.entries()).find(([_, c]) => c.visualIdx === i);
			columnKeys.set(key, { ...column, $width: `${width}px`, });
		}
		set({ columnKeys, });
	}


	const __onWindowResize = () => {
		if(__timer) clearTimeout(__timer);
		__timer = setTimeout(() => {
			_applyColumnOptions();
			_bindAccessors();
			if(__ref) {
				_bindRefs(__ref);
			}
			if(__timer) clearTimeout(__timer);
		}, 50);
	}

	( async ()=> await fetch() )();

	return {
		accessors,
		accessorData,
		data,
		pk,
		page: data.slice(0, pageSize),
		ready: false,
		paginating: paginate,
		currentPage: 1,
		totalPages: 1,
		totalItems: data.length,
		itemsPerPage: pageSize,
		firstPage: 1,
		lastPage: 1,
		widths: [],
		hiddenKeys: new Set<string>(),
		columnKeys: new Map(),
		columnWidths: [],
		deviceType: "MOBILE",
		emptyDataSet: data.length,
		firstPass: true,
		sort,
		sorters: {},
		sorting_asc: [],
		sorting_desc: [],
		rowMargin: "0",
		rowPadding: "0",
		headerRowMargin: "0",
		headerRowPadding: "0",
		bodyRowMargin: "0",
		bodyRowPadding: "0",
		toPage,
		nextPage,
		previousPage,
		setDeviceType,
		sortOn,
		_bindRefs,
	};

});


const keys: Record<string, boolean> = {}
const stores = TypedMap.create<ReturnType<typeof createTabularRasaStore<{}>>>();


export const getTabularRasaStore = <T extends object>( opts: string|TabularRasaStoreOptions<T> ): ReturnType<typeof createTabularRasaStore<T>> => {
	if(!opts) {
		throw new Error("TabularRasaStore options are required");
	}
	const retrieveOnly = typeof opts === "string";
	const id = retrieveOnly ? opts : opts.id;
	if(!id) {
		throw new Error("TabularRasaStore id is required");
	}

	keys[id] = keys[id] || true;

	if(retrieveOnly) {
		const store = stores.get(id);
		if(store) return store;
		throw new Error(`TabularRasaStore with id "${id}" not found`);
	}

	// @ts-expect-error - TODO: fix this up with a mapped type
	const store = stores.get<`${id}`, ReturnType<typeof createTabularRasaStore<T>>>(opts.id);
	// @ts-expect-error - TODO: fix this up with a mapped type
	if(store) return store;

	const newStore = createTabularRasaStore<T>(opts);
	stores.set(opts.id, newStore);

	return newStore;

};

